import {
  ActionIcon,
  Box,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMemo, useState } from "react";

type SectionType = "title" | "paragraph" | "numbered" | "references";

const sectionOptions = [
  { value: "title", label: "Section title" },
  { value: "paragraph", label: "Paragraph" },
  { value: "numbered", label: "Numbered list" },
  { value: "references", label: "References" },
];

const defaultNumberedItem = { title: "", text: "" };
const defaultReferenceRow = { text: "", link: "" };

interface AddSectionFormProps {
  onAddSection: (section: {
    type: SectionType;
    text?: string;
    items?: Array<
      | { number: number; title?: string; text: string }
      | { number: number; text: string; link?: string }
    >;
  }) => void;
}

export function AddSectionForm({ onAddSection }: AddSectionFormProps) {
  const [draftType, setDraftType] = useState<SectionType>("paragraph");
  const [paragraphText, setParagraphText] = useState("");
  const [titleText, setTitleText] = useState("");
  const [numberedDraft, setNumberedDraft] = useState<Array<{ title: string; text: string }>>([
    { ...defaultNumberedItem },
  ]);
  const [referenceDraft, setReferenceDraft] = useState<Array<{ text: string; link: string }>>([
    { ...defaultReferenceRow },
  ]);

  const canAddSection = useMemo(() => {
    if (draftType === "paragraph") {
      return paragraphText.trim().length > 0;
    }

    if (draftType === "title") {
      return titleText.trim().length > 0;
    }

    if (draftType === "numbered") {
      return numberedDraft.some((item) => item.text.trim().length > 0);
    }

    if (draftType === "references") {
      return referenceDraft.some((ref) => ref.text.trim().length > 0);
    }

    return false;
  }, [paragraphText, titleText, draftType, numberedDraft, referenceDraft]);

  const resetNumberedDraft = () => setNumberedDraft([{ ...defaultNumberedItem }]);
  const resetReferenceDraft = () => setReferenceDraft([{ ...defaultReferenceRow }]);

  const handleAddSection = () => {
    if (!canAddSection) {
      return;
    }

    if (draftType === "paragraph") {
      onAddSection({ type: "paragraph", text: paragraphText.trim() });
      setParagraphText("");
      return;
    }

    if (draftType === "title") {
      onAddSection({ type: "title", text: titleText.trim() });
      setTitleText("");
      return;
    }

    if (draftType === "numbered") {
      const items = numberedDraft
        .filter((item) => item.text.trim().length > 0)
        .map((item, index) => ({
          number: index + 1,
          title: item.title.trim() || undefined,
          text: item.text.trim(),
        }));

      if (items.length > 0) {
        onAddSection({ type: "numbered", items });
        resetNumberedDraft();
      }
      return;
    }

    if (draftType === "references") {
      const items = referenceDraft
        .filter((item) => item.text.trim().length > 0)
        .map((item, index) => ({
          number: index + 1,
          text: item.text.trim(),
          link: item.link.trim() || undefined,
        }));

      if (items.length > 0) {
        onAddSection({ type: "references", items });
        resetReferenceDraft();
      }
    }
  };

  const handleAddNumberedRow = () =>
    setNumberedDraft((prev) => [...prev, { ...defaultNumberedItem }]);

  const handleRemoveNumberedRow = (index: number) => {
    setNumberedDraft((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length ? next : [{ ...defaultNumberedItem }];
    });
  };

  const handleAddReferenceRow = () =>
    setReferenceDraft((prev) => [...prev, { ...defaultReferenceRow }]);

  const handleRemoveReferenceRow = (index: number) => {
    setReferenceDraft((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length ? next : [{ ...defaultReferenceRow }];
    });
  };

  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="md">
        <Title order={3}>Add Section</Title>
        <Select
          label="Section type"
          data={sectionOptions}
          value={draftType}
          onChange={(value) => value && setDraftType(value as SectionType)}
          searchable
        />

        {draftType === "paragraph" && (
          <Textarea
            label="Paragraph text"
            placeholder="Write a paragraph"
            minRows={4}
            value={paragraphText}
            onChange={(event) => setParagraphText(event.currentTarget.value)}
          />
        )}

        {draftType === "title" && (
          <TextInput
            label="Section title"
            placeholder="Add a title"
            value={titleText}
            onChange={(event) => setTitleText(event.currentTarget.value)}
          />
        )}

        {draftType === "numbered" && (
          <Stack gap="md">
            {numberedDraft.map((item, index) => (
              <Box key={index}>
                <TextInput
                  label={`Item ${index + 1} title (optional)`}
                  placeholder="Optional title"
                  value={item.title}
                  onChange={(event) =>
                    setNumberedDraft((prev) =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, title: event.currentTarget.value } : row,
                      ),
                    )
                  }
                  mb="xs"
                />
                <Group gap="xs" align="flex-start">
                  <Textarea
                    label={`Item ${index + 1} text`}
                    minRows={2}
                    placeholder="Item content"
                    value={item.text}
                    onChange={(event) =>
                      setNumberedDraft((prev) =>
                        prev.map((row, rowIndex) =>
                          rowIndex === index ? { ...row, text: event.currentTarget.value } : row,
                        ),
                      )
                    }
                    style={{ flex: 1 }}
                  />
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => handleRemoveNumberedRow(index)}
                    mt="xs"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Box>
            ))}
            <Button
              variant="default"
              size="sm"
              leftSection={<IconPlus size={16} />}
              onClick={handleAddNumberedRow}
              fullWidth
            >
              Add item
            </Button>
          </Stack>
        )}

        {draftType === "references" && (
          <Stack gap="md">
            {referenceDraft.map((item, index) => (
              <Box key={index}>
                <TextInput
                  label={`Reference ${index + 1} text`}
                  placeholder="Reference text"
                  value={item.text}
                  onChange={(event) =>
                    setReferenceDraft((prev) =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, text: event.currentTarget.value } : row,
                      ),
                    )
                  }
                  mb="xs"
                />
                <Group gap="xs" align="flex-start">
                  <TextInput
                    label="Link"
                    placeholder="https://"
                    value={item.link}
                    onChange={(event) =>
                      setReferenceDraft((prev) =>
                        prev.map((row, rowIndex) =>
                          rowIndex === index ? { ...row, link: event.currentTarget.value } : row,
                        ),
                      )
                    }
                    style={{ flex: 1 }}
                  />
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => handleRemoveReferenceRow(index)}
                    mt="xs"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Box>
            ))}
            <Button
              variant="default"
              size="sm"
              leftSection={<IconPlus size={16} />}
              onClick={handleAddReferenceRow}
              fullWidth
            >
              Add reference
            </Button>
          </Stack>
        )}

        <Button
          leftSection={<IconPlus size={16} />}
          onClick={handleAddSection}
          disabled={!canAddSection}
          fullWidth
        >
          Add section
        </Button>
      </Stack>
    </Paper>
  );
}
